import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as studyResearcherActions from '../study-researcher/study-researcher.actions';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { ResearcherService } from '../../services/researcher/researcher.service';
import { DashboardCamelCasePipe } from '../../../core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { ToastrService } from 'ngx-toastr';
import { DashboardSnakeCasePipe } from '../../../core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { Store } from '@ngrx/store';
import { AppState } from '../../../state';
import { selectSelectedStudy } from '../study/study.selectors';
import { CustomRouterNavigatedAction } from '../../../core/custom-route-serializer';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { STUDIES_ROUTES } from '../../studies/studies.routes';
import { PRIVILEGE_ACTIONS, PRIVILEGE_SECTIONS } from '../../../core/dashboard-constants';
import { User } from '../../../models/user';
import { Privilege } from '../../../models/privilege';
import { Study } from '../../../models/study';

export function transformPrivilegesFromApi(researcher: User, selectedStudy: Study): Privilege[] {
  const transformedPrivileges = [];
  const privilegeSections = Object.values(PRIVILEGE_SECTIONS);
  researcher?.privileges.forEach((privilege) => {
    if (
      privilege.studyId === selectedStudy.id &&
      !transformedPrivileges.find((transformedPrivilege) => transformedPrivilege.section === privilege.section)
    ) {
      transformedPrivileges.push(privilege);
    }
  });
  privilegeSections.forEach((privilegeSection) => {
    if (!transformedPrivileges.find((privilege) => privilege.section === privilegeSection)) {
      transformedPrivileges.push({
        id: 0,
        section: privilegeSection,
        researcherId: researcher.id,
        studyId: selectedStudy.id,
        action: PRIVILEGE_ACTIONS.HIDE,
      });
    }
  });
  transformedPrivileges.sort((p1, p2) => privilegeSections.indexOf(p1.section) - privilegeSections.indexOf(p2.section));
  return transformedPrivileges;
}

@Injectable()
export class StudyResearcherEffects {
  studyResearchersRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) =>
        route.payload.routerState.path.endsWith(STUDIES_ROUTES.RESEARCHERS)
      ),
      map((route: CustomRouterNavigatedAction) =>
        studyResearcherActions.setSingleStudyResearcherPageVisibility({
          singleResearcherVisible: false,
        })
      )
    )
  );

  getStudyResearchers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyResearcherActions.getStudyResearchers),
      withLatestFrom(this.store.select(selectSelectedStudy)),
      map(([{ researchers }, selectedStudy]) => {
        const researchersWithTransformedPrivileges = [];
        researchers.forEach((researcher) => {
          researchersWithTransformedPrivileges.push({
            ...researcher,
            privileges: transformPrivilegesFromApi(researcher, selectedStudy),
          });
        });
        return studyResearcherActions.getStudyResearchersSuccess({
          researchers: researchersWithTransformedPrivileges,
        });
      })
    )
  );

  getResearcher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyResearcherActions.getStudyResearcher),
      switchMap(({ email }) => {
        return this.researcherService.getResearcher(email).pipe(
          map((data) =>
            studyResearcherActions.getStudyResearcherSuccess({
              email,
              researcher: data[0],
              studyTypes: data[1],
            })
          ),
          catchError((err: HttpErrorResponse) => of(studyResearcherActions.getStudyResearcherError({ error: err })))
        );
      })
    )
  );

  getResearcherSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyResearcherActions.getStudyResearcherSuccess),
      withLatestFrom(this.store.select(selectSelectedStudy)),
      map(([{ email, researcher, studyTypes }, selectedStudy]) => {
        if (researcher) {
          if (selectedStudy?.researchers.find((researcherAtStudy) => researcherAtStudy.email === researcher.email)) {
            this.toastrService.error('You are trying to add a researcher who is already working on this study.');
            return studyResearcherActions.noopAction();
          } else if (!researcher.approved) {
            this.toastrService.error(
              'Please contact dashboard-bms@utwente.nl for further information.',
              'You are trying to add a researcher who is waiting for approval.'
            );
            return studyResearcherActions.noopAction();
          } else if (!researcher.active) {
            this.toastrService.error(
              'Request his access to this type of studies at dashboard-bms@utwente.nl.',
              'This researcher is currently deactivated, therefore, you can not add him/her to a study yet.'
            );
            return studyResearcherActions.noopAction();
          } else if (!studyTypes?.length || !studyTypes?.includes(selectedStudy.type)) {
            this.toastrService.error(
              'Request his access to this type of studies at dashboard-bms@utwente.nl.',
              'This researcher does not have access to this type of study, therefore, you can not add him/her to a study yet.'
            );
            return studyResearcherActions.noopAction();
          }
          return studyResearcherActions.getStudyResearcherByEmailSuccess({
            researcher: this.dashboardCamelCasePipe.keysToCamelCase(researcher),
          });
        }
        return studyResearcherActions.getStudyResearcherByEmailSuccess({ researcher });
      })
    )
  );

  addStudyResearcher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyResearcherActions.addStudyResearcher),
      withLatestFrom(this.store.select(selectSelectedStudy)),
      switchMap(([{ studyId, researcher }, selectedStudy]) => {
        return this.researcherService
          .addStudyResearcher(studyId, this.dashboardSnakeCase.keysToSnakeCase(researcher))
          .pipe(
            map((newResearcher) => {
              const newResearcherCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newResearcher);
              return studyResearcherActions.addStudyResearcherSuccess({
                newStudyResearcher: {
                  ...newResearcherCamelCase,
                  privileges: transformPrivilegesFromApi(newResearcherCamelCase, selectedStudy),
                },
              });
            }),
            catchError((err: HttpErrorResponse) => of(studyResearcherActions.addStudyResearcherError({ error: err })))
          );
      })
    )
  );

  addStudyResearcherSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyResearcherActions.addStudyResearcherSuccess),
      map(({ newStudyResearcher }) => {
        this.toastrService.success(
          `Researcher ${newStudyResearcher.firstname} ${newStudyResearcher.lastname} added successfully`
        );
        return studyResearcherActions.setSingleStudyResearcherPageVisibility({
          singleResearcherVisible: false,
        });
      })
    )
  );

  addStudyResearcherError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyResearcherActions.addStudyResearcherError),
        tap(() => this.toastrService.error(`Unable to add researcher`))
      ),
    { dispatch: false }
  );

  updateStudyResearcher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyResearcherActions.updateStudyResearcher),
      withLatestFrom(this.store.select(selectSelectedStudy)),
      switchMap(([{ studyId, researcher, addExisting }, selectedStudy]) => {
        return this.researcherService
          .updateStudyResearcher(studyId, this.dashboardSnakeCase.keysToSnakeCase(researcher))
          .pipe(
            map((updatedResearcher) => {
              let updatedResearcherCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(updatedResearcher);
              updatedResearcherCamelCase = {
                ...updatedResearcherCamelCase,
                privileges: transformPrivilegesFromApi(updatedResearcherCamelCase, selectedStudy),
              };
              return addExisting
                ? studyResearcherActions.addStudyResearcherSuccess({
                    newStudyResearcher: updatedResearcherCamelCase,
                  })
                : studyResearcherActions.updateStudyResearcherSuccess({
                    updatedStudyResearcher: updatedResearcherCamelCase,
                  });
            }),
            catchError((err: HttpErrorResponse) =>
              of(
                studyResearcherActions.updateStudyResearcherError({
                  error: err,
                })
              )
            )
          );
      })
    )
  );

  updateStudyResearcherSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyResearcherActions.updateStudyResearcherSuccess),
      map(({ updatedStudyResearcher }) => {
        this.toastrService.success(
          `Researcher ${updatedStudyResearcher.firstname} ${updatedStudyResearcher.lastname} updated successfully`
        );
        return studyResearcherActions.setSingleStudyResearcherPageVisibility({
          singleResearcherVisible: false,
        });
      })
    )
  );

  updateStudyResearcherError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyResearcherActions.updateStudyResearcherError),
      tap(() => this.toastrService.error(`Unable to update researcher`))
    )
  );

  deleteStudyResearcher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(studyResearcherActions.deleteStudyResearcher),
      switchMap(({ studyId, researcherId }) => {
        return this.researcherService.deleteStudyResearcher(studyId, researcherId).pipe(
          map(() =>
            studyResearcherActions.deleteStudyResearcherSuccess({
              deletedStudyResearcherId: researcherId,
            })
          ),
          catchError((err: HttpErrorResponse) =>
            of(
              studyResearcherActions.deleteStudyResearcherError({
                error: err,
              })
            )
          )
        );
      })
    )
  );

  deleteStudyResearcherSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyResearcherActions.deleteStudyResearcherSuccess),
        tap(({ deletedStudyResearcherId }) => this.toastrService.success(`Researcher deleted successfully`))
      ),
    { dispatch: false }
  );

  deleteStudyResearcherError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(studyResearcherActions.deleteStudyResearcherError),
        tap(() => this.toastrService.error(`Unable to delete researcher`))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private researcherService: ResearcherService,
    private dashboardSnakeCase: DashboardSnakeCasePipe,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private toastrService: ToastrService
  ) {}
}
